import type { AppState } from '../types'

const API_BASE = import.meta.env.VITE_API_BASE || '/api'

export interface ApiUser {
  id: string
  email: string
  createdAt: string
  isAdmin: boolean
}

export interface AdminUserSummary extends ApiUser {
  progressUpdatedAt: string | null
  feed: number
  tickets: number
  discoveryMarks: number
  sessions: number
  fish: number
  inventory: number
}

export interface ResourceGrant {
  feed?: number
  tickets?: number
  discoveryMarks?: number
}

interface AuthResponse {
  user: ApiUser
}

class ApiError extends Error {
  status: number

  constructor(status: number, message: string) {
    super(message)
    this.status = status
  }
}

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const response = await fetch(`${API_BASE}${path}`, {
    ...options,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(options.headers ?? {}),
    },
  })

  if (!response.ok) {
    let message = `Request failed (${response.status})`
    try {
      const body = await response.json() as { error?: string }
      if (body.error) message = body.error
    } catch {
      // Keep the status-based fallback.
    }
    throw new ApiError(response.status, message)
  }

  if (response.status === 204) return undefined as T
  return response.json() as Promise<T>
}

export async function getCurrentUser(): Promise<ApiUser | null> {
  try {
    const result = await request<AuthResponse>('/auth/me')
    return result.user
  } catch (error) {
    if (error instanceof ApiError && error.status === 401) return null
    throw error
  }
}

export async function register(email: string, password: string): Promise<ApiUser> {
  const result = await request<AuthResponse>('/auth/register', {
    method: 'POST',
    body: JSON.stringify({ email, password }),
  })
  return result.user
}

export async function login(email: string, password: string): Promise<ApiUser> {
  const result = await request<AuthResponse>('/auth/login', {
    method: 'POST',
    body: JSON.stringify({ email, password }),
  })
  return result.user
}

export async function logout(): Promise<void> {
  await request<void>('/auth/logout', { method: 'POST' })
}

export async function loadRemoteState(): Promise<AppState | null> {
  const result = await request<{ state: AppState | null }>('/state')
  return result.state
}

export async function saveRemoteState(state: AppState): Promise<void> {
  await request<void>('/state', {
    method: 'PUT',
    body: JSON.stringify({ state }),
  })
}


export async function listAdminUsers(): Promise<AdminUserSummary[]> {
  const result = await request<{ users: AdminUserSummary[] }>('/admin/users')
  return result.users
}

export async function grantAdminResources(userId: string, grant: ResourceGrant): Promise<AdminUserSummary> {
  const result = await request<{ user: AdminUserSummary }>(`/admin/users/${encodeURIComponent(userId)}/resources`, {
    method: 'POST',
    body: JSON.stringify(grant),
  })
  return result.user
}
